/**
 * GSAPアニメーション管理モジュール
 * フェードイン、テキスト分割、スクロール連動アニメーションを制御
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GSAP_CONFIG } from './config.js';

// GSAPプラグインの登録
gsap.registerPlugin(ScrollTrigger);

/**
 * フェードインアニメーションの初期化
 * .fadeIn クラス要素に対してスクロール連動フェードインを適用
 * @returns {void}
 */
export function gsapFadeIn() {
  document.querySelectorAll('.fadeIn').forEach((el) => {
    // data 属性のようにカスタム属性を使用して柔軟に設定できるようにする
    const delay = el.getAttribute('gsap-delay') || 0;
    const trigger = el.getAttribute('gsap-triggerContainer') || el;
    const start = el.getAttribute('gsap-start') || GSAP_CONFIG.scrollTriggerStart;

    // 事前に透明にしておく
    gsap.set(el, { autoAlpha: 0 });

    // フェードイン実行
    gsap.to(el, {
      scrollTrigger: {
        trigger,
        toggleActions: GSAP_CONFIG.basicToggleActions,
        start,
      },
      autoAlpha: 1,
      duration: 0.8,
      delay,
      ease: GSAP_CONFIG.basicEase,
    });
  });
}

/**
 * ズームインアニメーションの初期化
 * .zoomIn クラス要素に対して拡大しながらフェードインさせるアニメーションを適用
 * 現在ウェブサイトでトレンドのマイクロインタラクション演出として採用
 * @returns {void}
 */
export function gsapZoomIn() {
  document.querySelectorAll('.zoomIn').forEach((el) => {
    const delay = el.getAttribute('gsap-delay') || 0;
    const trigger = el.getAttribute('gsap-triggerContainer') || el;
    const start = el.getAttribute('gsap-start') || GSAP_CONFIG.scrollTriggerStart;
    const scaleFrom = parseFloat(el.getAttribute('gsap-scaleFrom')) || 0.8; // デフォルト0.8倍から

    // 初期状態を設定
    gsap.set(el, { autoAlpha: 0, scale: scaleFrom });

    // ズームイン実行
    gsap.to(el, {
      scrollTrigger: {
        trigger,
        toggleActions: GSAP_CONFIG.basicToggleActions,
        start,
      },
      autoAlpha: 1,
      scale: 1,
      duration: 0.8,
      delay,
      ease: GSAP_CONFIG.basicEase,
    });
  });
}

/**
 * テキスト分割アニメーションの初期化（SplitTextプラグイン不使用版）
 * .splitTextクラス要素に対して文字単位のフェードインアニメーションを適用
 * 手動でテキストを分割してアニメーションを実装
 */
export function gsapSplits() {
  const elements = document.querySelectorAll('.splitText');
  if (elements.length === 0) return;
  
  elements.forEach((el) => {
    const delay = el.getAttribute('gsap-delay') || 0;
    const trigger = el.getAttribute('gsap-triggerContainer') || el;
    const start = el.getAttribute('gsap-start') || GSAP_CONFIG.scrollTriggerStart;
    const stagger = el.getAttribute('gsap-stagger') || 0.05;
    
    // テキスト内容を取得
    const originalText = el.textContent;
    const chars = originalText.split('');
    
    // 元のテキストをクリア
    el.textContent = '';
    
    // 各文字をspan要素でラップ
    chars.forEach((char, index) => {
      const charSpan = document.createElement('span');
      // char = ' ' return;
      // スペース・改行は削除
      if (char === ' ' || char === '\n') return;
      charSpan.textContent = char; // スペースを非改行スペースに変換
      charSpan.className = 'split-char';
      charSpan.style.display = 'inline-block';
      el.appendChild(charSpan);
    });
    
    // 分割された文字要素を取得
    const charElements = el.querySelectorAll('.split-char');
    
    // 初期状態を設定
    gsap.set(charElements, { 
      autoAlpha: 0,
      // y: 2,
      x: -10,
      filter: "blur(10px)"
    });
    gsap.set(el, { opacity: 1 });
    
    // アニメーションを適用
    gsap.to(charElements, {
      scrollTrigger: {
        trigger,
        toggleActions: GSAP_CONFIG.basicToggleActions,
        start,
      },
      autoAlpha: 1,
      // y: 0,
      x: 0,
      filter: "blur(0px)",
      duration: 1.5,
      stagger,
      ease: GSAP_CONFIG.basicEase,
      delay,
    });
  });
}

/**
 * フェードインアップアニメーションの初期化
 * .fadeInUpクラス要素に対してスクロール連動フェードインを適用
 * @returns {void}
 */
export function gsapFadeInUp() {
  document.querySelectorAll('.fadeInUp').forEach((el) => {
    const delay = el.getAttribute('gsap-delay') || 0;
    const trigger = el.getAttribute('gsap-triggerContainer') || el;
    const start = el.getAttribute('gsap-start') || GSAP_CONFIG.scrollTriggerStart;

    // 下方向に 60px ずらして非表示に、ブラー効果を追加
    gsap.set(el, { 
      y: 60, 
      autoAlpha: 0,
      filter: "blur(8px)"
    });

    gsap.to(el, {
      scrollTrigger: {
        trigger,
        toggleActions: GSAP_CONFIG.basicToggleActions,
        start,
      },
      autoAlpha: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 0.6,
      delay,
      ease: 'power2.out',
    });
  });
}

/**
 * 固定バナー表示切り替えアニメーション
 * @param {Element} triggerElement - トリガー要素
 * @param {Element} targetElement - 対象要素
 * @param {Element} endElement - 終了トリガー要素
 */
export function toggleFixedBanner(triggerElement, targetElement, endElement) {
  if (triggerElement && targetElement && endElement) {
    gsap.fromTo(
      targetElement,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        scrollTrigger: {
          trigger: triggerElement,
          start: 'top bottom',
          endTrigger: endElement,
          end: 'top bottom',
          toggleActions: 'play reverse play reverse',
        },
      }
    );
  }
}

/**
 * 飛行機アニメーションの初期化
 * GSAPタイムラインを使用して飛行軌跡の点線が順次表示され、最後に飛行機が現れるアニメーション
 */
export function setupPlaneAnimation() {
  const planeSvg = document.querySelector('#plane-svg');
  if (!planeSvg) return;

  const trailRects = planeSvg.querySelectorAll('#flight-trail rect');
  const planeIcon = planeSvg.querySelector('.plane_icon');
  
  if (trailRects.length === 0 || !planeIcon) return;

  // 初期状態を設定
  gsap.set(trailRects, { 
    opacity: 0
  });
  gsap.set(planeIcon, { 
    opacity: 0, 
    x: '-100%', 
    scale: 0.8 
  });

  /**
   * 飛行機アニメーションのタイムライン作成
   */
  function createPlaneTimeline() {
    const timeline = gsap.timeline({ paused: true });

    // 飛行軌跡を順次表示
    trailRects.forEach((rect, index) => {
      timeline.to(rect, {
        opacity: 1,
        duration: 0.15,
        ease: "power2.out"
      }, index * 0.025); // 0.05秒間隔で順次表示

      // 点滅効果を追加（表示から0.1秒後）
      timeline.to(rect, {
        opacity: 0.3,
        duration: 0.3,
        yoyo: true,
        repeat: -1,
        ease: "power2.inOut"
      }, index * 0.05 + 0.1);
    });

    // 最後に飛行機を表示
    timeline.to(planeIcon, {
      opacity: 1,
      x: 0,
      scale: 1,
      duration: 0.4,
      ease: "power2.out"
    }, trailRects.length * 0.025 + 0.2);

    return timeline;
  }

  // タイムラインを作成
  const planeTimeline = createPlaneTimeline();

  // ScrollTriggerを使用してビューポートに入ったときにアニメーション開始
  ScrollTrigger.create({
    trigger: planeSvg,
    start: GSAP_CONFIG.scrollTriggerStart,
    end: 'bottom 20%',
    onEnter: () => {
      planeTimeline.restart();
    },
    onLeave: () => {
      // planeTimeline.pause();
    },
    onEnterBack: () => {
      // planeTimeline.restart();
    },
    onLeaveBack: () => {
      // planeTimeline.pause();
      // // 初期状態にリセット
      // gsap.set(trailRects, { 
      //   opacity: 0
      // });
      // gsap.set(planeIcon, { 
      //   opacity: 0, 
      //   x: -50, 
      //   scale: 0.8 
      // });
    }
  });

} 
